/**
 * Arena asset manifest — the file paths the match needs warm, and nothing else.
 *
 * Why it is its own module: the shell (`shell/arena-handoff.ts`) starts warming the HTTP cache
 * the instant a player presses PLAY, one page before the arena exists. To do that it has to
 * know which files to fetch — and the two tables that used to own those strings live beside
 * three.js (`render-settings.ts` feeds `viewmodel.ts`, `character.ts` builds meshes), so
 * asking them would drag the whole renderer into the menu bundle for the sake of 23 strings.
 *
 * So the strings moved here and the renderer reads them back. That is the point: one source of
 * truth. A shell warming one path while the arena loads another is a cache miss that looks
 * exactly like a slow asset host, and nothing in either file would have said so.
 *
 * Imports nothing, deliberately. Anything added here must stay data.
 */

/** Weapon viewmodel/held-weapon GLBs, keyed by `WeaponDefinition.id` — the table
 * `WEAPON_VIEWMODELS` (render-settings.ts) reads its `model` from. Ids 8/19/20 share
 * `grenade.glb`; the duplicate is deduped by `weaponPrefetchUrls()`. */
export const WEAPON_MODEL_URLS: Record<number, string> = {
  0: '/game/models/synty/weapons/ar.glb',
  1: '/game/models/synty/weapons/smg.glb',
  2: '/game/models/synty/weapons/shotgun.glb',
  3: '/game/models/synty/weapons/sniper.glb',
  4: '/game/models/synty/weapons/pistol.glb',
  5: '/game/models/synty/weapons/carbine.glb',
  6: '/game/models/synty/weapons/lmg.glb',
  7: '/game/models/synty/weapons/battle-rifle.glb',
  8: '/game/models/synty/weapons/grenade.glb',
  9: '/game/models/synty/weapons/revolver.glb',
  10: '/game/models/synty/weapons/smg-lite.glb',
  11: '/game/models/synty/weapons/ak.glb',
  12: '/game/models/synty/weapons/dmr.glb',
  13: '/game/models/synty/weapons/heavy-sniper.glb',
  14: '/game/models/synty/weapons/scout.glb',
  15: '/game/models/synty/weapons/snub.glb',
  16: '/game/models/synty/weapons/pistol-sd.glb',
  17: '/game/models/synty/weapons/pistol-auto.glb',
  18: '/game/models/synty/weapons/smoke-grenade.glb',
  19: '/game/models/synty/weapons/flashbang.glb',
  20: '/game/models/synty/weapons/molotov.glb',
};

/**
 * The character GLBs that are loaded for *every* match — the two team defaults, which is what
 * `preloadCharacterModels` pulls and therefore all the boot screen prefetches. Cosmetic skins
 * are deliberately absent (rare, gated, ~7MB nobody may wear); see `preloadCharacterModels`.
 *
 * Keyed by `CHARACTER_MODEL_SPECS` key, and `TEAM_DEFAULT_MODEL_KEY` is typed against those
 * keys — so a team default that is not in this manifest is a compile error rather than an
 * asset the shell silently fails to warm.
 */
export const TEAM_DEFAULT_CHARACTER_MODELS = {
  'synty-rifleman': '/game/models/synty/characters/military-male.glb',
  'synty-merc': '/game/models/synty/characters/mercenary-male.glb',
} as const;

export type TeamDefaultModelKey = keyof typeof TEAM_DEFAULT_CHARACTER_MODELS;

/** Deduped weapon models. Both prefetch callers use this, never the record directly. */
export function weaponPrefetchUrls(): string[] {
  return [...new Set(Object.values(WEAPON_MODEL_URLS))];
}

/** The team-default character models, deduped for symmetry with the weapons. */
export function characterModelPrefetchUrls(): string[] {
  return [...new Set(Object.values(TEAM_DEFAULT_CHARACTER_MODELS))];
}

/** Everything a match pulls before it plays smoothly, weapons first — the order the arena's
 * own boot screen fetches in, kept so the shell warms the same files in the same order and a
 * capped hand-off leaves the *later* files cold rather than a random half of both sets. */
export function arenaPrefetchUrls(): string[] {
  return [...weaponPrefetchUrls(), ...characterModelPrefetchUrls()];
}
